import { memo } from 'react'

import { Box, Button, Group, Stack, Text, Title } from '@mantine/core'

import { IconArrowRight } from '@repo/common/components/Icons'
import {
  TokenAndTextRender,
  TokenRender,
} from '@repo/common/components/TokenRender'
import {
  countFormatter,
  getMaxFractionDigits,
  usdFormatter,
} from '@repo/common/helpers/formatters'
import { useActiveFundInfo } from '@repo/common/queries/funds'
import {
  type LimitOrderType,
  useEarlyLimitOrderExit,
} from '@repo/common/queries/limit_orders'

import { get_receiving_tokens } from '@/flows/LimitOrder/useLimitOrderFields'

import { LinkButton } from '../LinkButton'

// eslint-disable-next-line max-lines-per-function
export const LimitOrderRow = memo<LimitOrderType>(function LimitOrderRow({
  id,
  fund_symbol,
  sell_symbol,
  tokens,
  price,
  tick,
}) {
  const { data: fund } = useActiveFundInfo({ symbol: fund_symbol })

  const early_close = useEarlyLimitOrderExit({ fund_symbol })

  const receiving_symbol = sell_symbol == 'USDC' ? fund_symbol : 'USDC'
  return (
    <Group gap="xl" justify="flex-start">
      <Group w="190px" wrap="nowrap">
        <TokenRender symbol={fund_symbol} size="var(--mantine-font-size-xl2)" />
        <Stack gap="0">
          <Text size="lg" fw="bold" lh="1">
            {fund?.name}
          </Text>
          <Text size="sm">{fund_symbol}</Text>
        </Stack>
      </Group>
      <Box flex="1 1" />
      <Stack gap="0" align="self-start">
        <Title order={2} fz="xs" lh="2">
          Sell Price
        </Title>
        <Text style={{ textWrap: 'nowrap' }} lh="var(--mantine-font-size-xl)">
          {usdFormatter(price)}
        </Text>
      </Stack>
      <Stack gap="0" align="self-start">
        <Title order={2} fz="xs" lh="2">
          Selling
        </Title>
        <Group h="var(--mantine-font-size-xl)" align="center">
          <TokenAndTextRender size="md" symbol={sell_symbol}>
            {countFormatter(tokens, {
              maximumFractionDigits: getMaxFractionDigits(sell_symbol),
            })}
          </TokenAndTextRender>
        </Group>
      </Stack>
      <Stack>
        <IconArrowRight size={30} />
      </Stack>
      <Stack gap="0" align="self-start">
        <Title order={2} fz="xs" lh="2">
          Receiving Once Closed
        </Title>
        <Group h="var(--mantine-font-size-xl)" align="center">
          <TokenAndTextRender size="md" symbol={receiving_symbol}>
            {countFormatter(
              get_receiving_tokens({
                symbol_in: sell_symbol,
                price,
                sell_tokens: tokens,
              }),
              {
                maximumFractionDigits: getMaxFractionDigits(receiving_symbol),
              },
            )}
          </TokenAndTextRender>
        </Group>
      </Stack>
      <Group gap="0">
        <LinkButton
          variant="subtle"
          size="sm"
          to="/trade/$fund_symbol/limit_order"
          params={{
            fund_symbol,
          }}
          search={{
            price,
            tick,
            sell: sell_symbol == 'USDC' ? 'USDC' : 'TOKEN',
          }}
        >
          Add
        </LinkButton>
        <Button
          variant="subtle"
          size="sm"
          loading={early_close.isPending}
          disabled={!early_close.isReady}
          onClick={() => early_close.mutate({ order_token_id: id })}
        >
          Exit Early
        </Button>
      </Group>
    </Group>
  )
})
